import {
  computed,
  ChangeDetectionStrategy,
  Component,
  inject,
} from '@angular/core'
import { Router, RouterModule } from '@angular/router'

import { Enums, Hunt, Mitre } from '@cyborg-monorepo/hunter/common'
import {
  BadgeComponent,
  ButtonComponent,
  ErrorComponent,
  TypographyComponent,
  TagComponent,
  BreadcrumbListComponent,
  ButtonMenuComponent,
  DropdownMenuItemComponent,
  DialogService,
  DialogRef,
  SegmentedProgressBarComponent,
} from '@hyperkit'

import {
  HuntViewService,
  HuntEditorService,
  NavigationService,
  BreadcrumbService,
  SessionService,
} from '@hunt/services'

import { HuntAPIService } from '../../shared/services'
import { HuntEditorComponent } from '../details/components/hunt-editor'
import { ArchiveHuntDialogComponent } from '../dialogs/archive-hunt-dialog/archive-hunt-dialog.component'
import { EditHuntDialogComponent } from '../dialogs/edit-hunt-dialog/edit-hunt-dialog.component'
import {
  ReportSectionComponent,
  Section,
} from '../report-section/report-section.component'

@Component({
  standalone: true,
  selector: 'hunt-edit-report',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    RouterModule,
    ButtonComponent,
    TypographyComponent,
    BadgeComponent,
    ErrorComponent,
    TagComponent,
    ReportSectionComponent,
    BreadcrumbListComponent,
    HuntEditorComponent,
    ButtonMenuComponent,
    DropdownMenuItemComponent,
    SegmentedProgressBarComponent,
  ],
  templateUrl: './hunt-edit-report.component.html',
  styleUrls: ['hunt-edit-report.component.scss'],
})
export class HuntEditReportComponent {
  readonly huntView = inject(HuntViewService)
  readonly huntEditor = inject(HuntEditorService)
  private readonly navSvc = inject(NavigationService)
  private readonly huntApi = inject(HuntAPIService)
  private readonly dialogService = inject(DialogService)
  private readonly router = inject(Router)
  private readonly breadcrumb = inject(BreadcrumbService)
  readonly session = inject(SessionService)

  readonly loaded = computed(
    () => !this.session.loading() && this.session.activeHunt() !== null,
  )

  /** Breadcrumbs for the edit-report view */
  public readonly breadcrumbs = this.breadcrumb.editReport
  public error: string

  /** Which section under edit-report is active */
  public readonly selectedSectionId = this.navSvc.selectedEditReportSection

  /** Sidebar sections: overview, technical-summary, conclusion */
  public readonly reportSections = computed<Section[]>(() => {
    const pkgs = this.huntView.huntPackages()
    const techChildren = pkgs
      .filter((pkg) => (pkg.reportAnalysis ?? []).length > 0)
      .map((pkg) => ({ id: pkg.UUID, title: pkg.title }))
    return [
      { id: 'overview', title: 'Overview' },
      {
        id: 'technical-summary',
        title: 'Technical Summary',
        children: techChildren,
      },
      { id: 'conclusion', title: 'Conclusion' },
    ]
  })

  /** Navigate when user clicks a section in the sidebar */
  public onSelectSection(sectionId?: string): void {
    if (this.huntView.huntPackages().some((p) => p.UUID === sectionId)) {
      this.navSvc.navigateToEditReportTechSummary(sectionId!)
    } else if (sectionId === 'overview') {
      this.navSvc.navigateToEditReportOverview()
    } else if (sectionId === 'conclusion') {
      this.navSvc.navigateToEditReport(['conclusion'])
    }
  }

  /** Open the “Edit Hunt” dialog */
  async editHunt() {
    const hunt = this.session.activeHunt()
    if (!hunt) return
    const ref = this.dialogService.open(EditHuntDialogComponent, { data: hunt })
    ref.afterClosed().subscribe(async (hunt) => {
      await this.session.loadHunt((hunt as unknown as Hunt).id)
    })
  }

  /** Archive the hunt and go back to list */
  async archiveHunt() {
    const hunt = this.session.activeHunt()
    if (!hunt) return
    const ref: DialogRef = this.dialogService.open<Hunt>(
      ArchiveHuntDialogComponent,
      {
        data: hunt,
      },
    )

    ref.afterClosed().subscribe(async (archived: Hunt | undefined) => {
      if (archived) {
        await this.session.loadHunt(archived.id, true)
        this.router.navigate(['/hunt/hunts'])
      }
    })
  }

  /** Handle the dropdown menu actions */
  handleMenuAction(event: { value: 'update' | 'archive' }) {
    if (event.value === 'update') this.editHunt()
    else if (event.value === 'archive') this.archiveHunt()
  }

  /** Finalize & submit edited report */
  public onSubmit(): void {
    const id = this.huntView.huntId()
    this.huntApi.finalizeHuntReport(id).subscribe()
  }

  /** Save draft and return to workbench */
  public async backToHunt(): Promise<void> {
    await this.huntEditor.updateStatus(Enums.StatusEnum.IN_PROGRESS)
    await this.huntEditor.updateEditing(true)
    this.navSvc.navigateToWorkbench()
  }

  /** Complete report and navigate to final-report */
  public async done(): Promise<void> {
    await this.huntEditor.updateStatus(Enums.StatusEnum.DONE)
    await this.huntEditor.updateEditing(false)
    this.navSvc.navigateToFinalReport()
  }

  /** Utility for rendering MITRE techniques */
  public getMitres(mitres: Mitre) {
    return mitres.mitre_attack_techniques.map((tech, i) => ({
      id: mitres.mitre_attack_technique_ids[i],
      technique: tech,
    }))
  }
}
